import type { FC, PropsWithChildren, ReactNode } from "react";
import { useCallback, useId, useState } from "react";

import { Tooltip } from "@ui/components/Tooltip";
import classNames from "classnames";

import { ChevronDownIcon } from "@jti/brand-icons/chevron-down";
import { ChevronUpIcon } from "@jti/brand-icons/chevron-up";

import styles from "./Expandable.module.scss";

export type ExpandableProps = {
  label: string;
  defaultExpanded?: boolean;
  className?: string;
  disabled?: boolean;
  disabledTooltip?: ReactNode;
};

export const Expandable: FC<PropsWithChildren<ExpandableProps>> = ({
  label,
  defaultExpanded = false,
  disabled = false,
  disabledTooltip,
  children,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const uniqueId = useId();
  const buttonId = `expandable-btn-${uniqueId}`;
  const panelId = `expandable-panel-${uniqueId}`;

  const toggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const isOpen = isExpanded && !disabled;
  const ChevronIcon = isOpen ? ChevronUpIcon : ChevronDownIcon;

  return (
    <div className={classNames(styles.expandable, { [styles.disabled]: disabled })}>
      <div className={styles.header}>
        <button
          type="button"
          id={buttonId}
          className={styles.trigger}
          aria-expanded={disabled ? undefined : isOpen}
          aria-controls={disabled ? undefined : panelId}
          disabled={disabled}
          onClick={toggle}
        >
          <span className={styles.label}>{label}</span>
          {!disabled && <ChevronIcon aria-hidden="true" focusable="false" className={styles.icon} />}
        </button>
        {disabled && disabledTooltip && (
          <Tooltip behaviour="hover" content={disabledTooltip} wrapperClassName={styles.icon} />
        )}
      </div>
      {isOpen && (
        <div id={panelId} role="region" aria-labelledby={buttonId} className={styles.content}>
          {children}
        </div>
      )}
    </div>
  );
};
